import axios from "axios";
import axioInstance from "../../../../../../../../../custom_utilities/AxiosInstance/axiosInstance";
import { url_store_package_view } from "../../../../../../../../../custom_utilities/api_services";
import {
  findPackageById,
  filterValidVariantOptions,
  isPackageSubscribed,
  normalizeStorePackageViewResponse,
} from "./storePackageUtils";
import {
  storePriceToastMessage,
  toastifyMessage,
} from "../../../../../../../../../custom_utilities/toastify_config";
import { url_59 } from "../../../../../../../../../custom_utilities/api_services";
import {
  trackWebEngageCouponApplied,
  trackWebEngageCouponFailed,
} from "../../../../../../../../../custom_utilities/webengage/webengageService";

const calculateFinalAmount = (priceDetails) => {
  const coursePrice = Number(priceDetails?.course_price ?? 0);
  const discount = Number(priceDetails?.discount ?? 0);
  return (coursePrice - discount).toFixed(2);
};

export const fetchStorePackageView = async (packageId) => {
  const response = await axioInstance.get(
    `${url_store_package_view}?package_id=${packageId}`
  );

  if (response.data?.status !== 200) {
    throw new Error(response.data?.message || "Failed to load package details.");
  }

  const normalized = normalizeStorePackageViewResponse(response.data?.data);
  if (!normalized) {
    throw new Error("No data received from API");
  }

  return normalized;
};

const selectPackage = (
  packageId,
  packageVariants,
  setSelectedPackage,
  packageData = null
) => {
  const parentId = packageData?.PackageInstallmentDetails?.package_id;
  const options = filterValidVariantOptions(
    packageVariants?.quick_variants_selection_options ?? [],
    parentId
  );

  const packageFromOptions = findPackageById(
    packageId,
    options,
    packageVariants
  );
  const installmentDetails = packageData?.PackageInstallmentDetails;
  const defaultPackage = packageVariants?.quick_variants_selection_default;
  const defaultInOptions =
    defaultPackage &&
    options.find(
      (option) => Number(option.package_id) === Number(defaultPackage.package_id)
    );

  const pickIfAvailable = (pkg) =>
    pkg && !isPackageSubscribed(pkg) ? pkg : null;

  // Prefer first not-yet-purchased option (skip bought → 2nd, 3rd, ...)
  const firstAvailableOption =
    options.find((option) => !isPackageSubscribed(option)) || null;

  const resolvedPackage =
    pickIfAvailable(packageFromOptions) ||
    pickIfAvailable(defaultInOptions) ||
    firstAvailableOption ||
    pickIfAvailable(
      Number(installmentDetails?.package_id) === Number(packageId)
        ? installmentDetails
        : null
    ) ||
    null;

  setSelectedPackage(resolvedPackage);
  return resolvedPackage;
};

export const fetchPackageData = async ({
  packageId,
  isInitialLoad,
  setPackageData,
  setStoreCurrentPackageDetails,
  setFinalAmount,
  setInitialPackageVariants,
  setSelectedPackage,
  startLoading,
  finishLoading,
}) => {
  try {
    startLoading();

    const data = await fetchStorePackageView(packageId);

    setPackageData(data);
    setStoreCurrentPackageDetails(data);

    if (isInitialLoad) {
      const variants = data.package_quick_variants;
      setInitialPackageVariants({
        ...variants,
        quick_variants_selection_options: filterValidVariantOptions(
          variants?.quick_variants_selection_options ?? [],
          data?.PackageInstallmentDetails?.package_id
        ),
      });
    }

    const resolvedPackage = selectPackage(
      packageId,
      data.package_quick_variants,
      setSelectedPackage,
      data
    );

    // Landing on a parent/list package: auto-select first option and load its price
    // so Proceed to Pay appears (same outcome as manually picking the first radio).
    if (
      isInitialLoad &&
      resolvedPackage?.package_id &&
      Number(resolvedPackage.package_id) !== Number(packageId)
    ) {
      const selectedData = await fetchStorePackageView(
        resolvedPackage.package_id
      );
      setPackageData(selectedData);
      setStoreCurrentPackageDetails(selectedData);
    }
  } catch (error) {
    console.error("Error fetching package data:", error.message || error);
    toastifyMessage({
      status: "error",
      message:
        error.message || "Failed to load package details. Please try again.",
    });
  } finally {
    finishLoading();
  }
};

// applyCoupon - Correct discount calculation
export const applyCoupon = async ({
  couponCode,
  userId,
  packageId,
  selectedPackage,
  packageData,
  setFinalAmount,
  setCouponDiscount,
  setAddonDiscount,
  startLoading,
  finishLoading,
  handleCloseCoupon,
  setIsCouponApplied,
  finalAmount,
  orderId,
}) => {
  if (!couponCode) {
    storePriceToastMessage({
      status: "error",
      message: "Please enter a coupon code.",
    });
    trackWebEngageCouponFailed({ coupon_code: couponCode, source: "store" });
    setCouponDiscount(0);
    setFinalAmount(calculateFinalAmount(packageData?.price_details) || "0.00");
    return;
  }

  if (!userId || !packageId) {
    console.error("Invalid userId or packageId:", { userId, packageId });
    storePriceToastMessage({
      status: "error",
      message: "Invalid user or package information.",
    });
    trackWebEngageCouponFailed({ coupon_code: couponCode, source: "store" });
    setCouponDiscount(0);
    setFinalAmount(calculateFinalAmount(packageData?.price_details) || "0.00");
    return;
  }

  try {
    startLoading();

    const payload = {
      is_coupon: true,
      user_id: userId,
      package_id: packageId,
      amount: finalAmount,
      coupon_code: couponCode,
    };

    if (orderId) {
      payload.order_id = orderId;
    }

    const response = await axios.post(url_59, payload);

    if (response.data.status === 200 && response.data.data.status === 200) {
      const updatedAmount = Number(response.data.data.updated_amount);

      const previousAmount = Number(
        finalAmount || calculateFinalAmount(packageData?.price_details)
      );
      const actualCouponDiscount = Math.max(0, previousAmount - updatedAmount);

      setFinalAmount(updatedAmount.toFixed(2));
      setCouponDiscount(actualCouponDiscount.toFixed(2));
      setAddonDiscount(0);
      setIsCouponApplied(true);

      trackWebEngageCouponApplied({
        coupon_code: couponCode,
        order_coupon_code: couponCode,
        discount_amount: actualCouponDiscount,
        cart_value_before_discount: previousAmount,
        cart_value_after_discount: updatedAmount,
        source: "store",
        coupon_type: "package",
      });

      storePriceToastMessage({
        status: "success",
        message: response.data.data.message,
      });
      handleCloseCoupon();
    } else {
      setCouponDiscount(0);
      setAddonDiscount(0);
      setFinalAmount(calculateFinalAmount(packageData?.price_details) || "0.00");
      setIsCouponApplied(false);
      trackWebEngageCouponFailed({ coupon_code: couponCode, source: "store" });
      storePriceToastMessage({
        status: "error",
        message: response.data.data.message || response.data.message,
      });
    }
  } catch (error) {
    console.error("Error applying coupon:", error);

    setCouponDiscount(0);
    setAddonDiscount(0);
    setFinalAmount(calculateFinalAmount(packageData?.price_details) || "0.00");
    setIsCouponApplied(false);

    trackWebEngageCouponFailed({ coupon_code: couponCode, source: "store" });

    storePriceToastMessage({
      status: "error",
      message: error.message || "Failed to apply coupon. Please try again.",
    });
  } finally {
    finishLoading();
  }
};
